import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  countries: [],
  selectedCountry: null,
  selectedLeague: null,
  selectedCountryLeague: null, // Will contain the full league object with country context
  isCountryLoading: false,
  isLeagueLoading: false,
  expandedCountries: [], // Track which countries are expanded in UI
};

const countrySlice = createSlice({
  name: "country",
  initialState,
  reducers: {
    // Set all countries data
    setCountries: (state, action) => {
      state.countries = action.payload;
    },

    // Set selected country
    setSelectedCountry: (state, action) => {
      state.selectedCountry = action.payload;
      // Clear selected league when country changes
      state.selectedLeague = null;
      state.selectedCountryLeague = null;
    },

    // Set selected league with country context
    setSelectedCountryLeague: (state, action) => {
      const { country, league } = action.payload;

      // Find the country in the state
      const foundCountry = state.countries.find((c) => c.country === country);
      // console.log("Found League:", JSON.parse(JSON.stringify(foundCountry)));

      if (foundCountry && league) {
        state.selectedCountryLeague = {
          country: country,
          countryData: foundCountry,
          league: league,
          leagueId: league.league_id,
          leagueName: league.leagueName,
          gamesCount: league.games_count,
        };

        state.selectedCountry = country;
        state.selectedLeague = league;
      }
    },

    // Set only the league (when you have league context)
    setSelectedLeague: (state, action) => {
      state.selectedLeague = action.payload;
    },

    // Clear selections
    clearSelections: (state) => {
      state.selectedCountry = null;
      state.selectedLeague = null;
      state.selectedCountryLeague = null;
    },

    // Loading states
    setCountryLoading: (state, action) => {
      state.isCountryLoading = action.payload;
    },

    setLeagueLoading: (state, action) => {
      state.isLeagueLoading = action.payload;
    },

    // UI state for expanded countries
    setExpandedCountries: (state, action) => {
      state.expandedCountries = action.payload;
    },

    toggleCountryExpansion: (state, action) => {
      const countryName = action.payload;
      const index = state.expandedCountries.indexOf(countryName);

      if (index > -1) {
        state.expandedCountries.splice(index, 1);
      } else {
        state.expandedCountries.push(countryName);
      }
    },

    // Helper to get leagues for a specific country
    getCountryLeagues: (state, action) => {
      const countryName = action.payload;
      const country = state.countries.find((c) => c.country === countryName);
      return country ? country.leagues || [] : [];
    },

    // Update specific country's leagues (useful for dynamic loading)
    updateCountryLeagues: (state, action) => {
      const { countryName, leagues } = action.payload;
      const countryIndex = state.countries.findIndex(
        (c) => c.country === countryName
      );

      if (countryIndex > -1) {
        state.countries[countryIndex].leagues = leagues;
        state.countries[countryIndex].leagues_count = leagues.length.toString();
      }
    },

    // Add a new country
    addCountry: (state, action) => {
      const newCountry = action.payload;
      const existingIndex = state.countries.findIndex(
        (c) => c.country === newCountry.country
      );

      if (existingIndex > -1) {
        // Update existing country
        state.countries[existingIndex] = newCountry;
      } else {
        // Add new country
        state.countries.push(newCountry);
      }
    },

    // Remove a country
    removeCountry: (state, action) => {
      const countryName = action.payload;
      state.countries = state.countries.filter(
        (c) => c.country !== countryName
      );

      // Clear selections if removed country was selected
      if (state.selectedCountry === countryName) {
        state.selectedCountry = null;
        state.selectedLeague = null;
        state.selectedCountryLeague = null;
      }
    },
  },
});

// Actions
export const {
  setCountries,
  setSelectedCountry,
  setSelectedCountryLeague,
  setSelectedLeague,
  clearSelections,
  setCountryLoading,
  setLeagueLoading,
  setExpandedCountries,
  toggleCountryExpansion,
  getCountryLeagues,
  updateCountryLeagues,
  addCountry,
  removeCountry,
} = countrySlice.actions;

// Selectors
export const selectCountries = (state) => state.country.countries;
export const selectSelectedCountry = (state) => state.country.selectedCountry;
export const selectSelectedLeague = (state) => state.country.selectedLeague;
export const selectSelectedCountryLeague = (state) =>
  state.country.selectedCountryLeague;
export const selectIsCountryLoading = (state) => state.country.isCountryLoading;
export const selectIsLeagueLoading = (state) => state.country.isLeagueLoading;
export const selectExpandedCountries = (state) =>
  state.country.expandedCountries;

// Complex selectors
export const selectCountryByName = (countryName) => (state) =>
  state.country.countries.find((c) => c.country === countryName);

export const selectLeaguesByCountry = (countryName) => (state) => {
  const country = state.country.countries.find(
    (c) => c.country === countryName
  );
  return country ? country.leagues || [] : [];
};

export const selectLeagueById = (leagueId) => (state) => {
  for (const country of state.country.countries) {
    if (country.leagues) {
      const league = country.leagues.find((l) => l.league_id === leagueId);
      if (league) {
        return { country: country.country, league };
      }
    }
  }
  return null;
};

export const selectTotalLeaguesCount = (state) =>
  state.country.countries.reduce(
    (total, country) => total + (country.leagues ? country.leagues.length : 0),
    0
  );

export const selectCountriesWithLeagues = (state) =>
  state.country.countries.filter(
    (country) => country.leagues && country.leagues.length > 0
  );

export const countryReducer = countrySlice.reducer;
